/**
 * CitationStrip - inline corpus-cited evidence list under an NBA card
 * (PROD-590).
 *
 * Renders one row per `RetrievedChunk` returned by the citation resolver
 * (PROD-590). Each row carries:
 *   - A short citation label (the source citation truncated at the first
 *     period or the configured cap)
 *   - The chunk's section, when present
 *   - A relevance / distance pill so the clinician can see how far the
 *     chunk is from the query
 *   - An "open" button that triggers the `onOpen(chunk)` callback. The
 *     consumer (NbaCard) lifts state to host a `<CitationDrawer>` with
 *     the full chunk text + provenance.
 *
 * HC3 citation discipline: the strip ALWAYS renders when the resolver
 * returned `source: 'live' | 'demo'` with at least one chunk. When the
 * resolver returns `source: 'empty'`, the consumer renders the empty
 * state directly (the strip is not shown). This component is therefore
 * pure presentation; the empty-state branch is the consumer's contract.
 *
 * HC8 corpus-as-floor: chunk metadata reaches the row verbatim. The strip
 * does NOT mutate the chunk text or distance.
 *
 * HC2: every color, spacing, radius value resolves through Signal DS
 * tokens (no raw hex). HC5: zero em / en dashes anywhere.
 *
 * HC6 a11y:
 *   - The strip is `<section role="group" aria-labelledby={...}>`
 *     introducing the citation list.
 *   - Each row is a `<li>` carrying `data-chunk-id` for tests.
 *   - The "open" button is a real `<button>` with an `aria-label` that
 *     names the chunk (so SR users hear which citation is being opened).
 *   - The relevance pill carries `aria-label` so SR users hear the
 *     numeric reading instead of the visual percent.
 */
import { useId } from 'react';
import type { RetrievedChunk } from '../services/corpus-retrieval';
import type { CitationSet } from '../services/citation-resolver';

export interface CitationStripProps {
  /** The resolved citation set for the host NBA card. */
  citations: CitationSet;
  /** NBA candidate id, used in test ids + the strip heading copy. */
  nbaId: string;
  /**
   * Called when the clinician clicks "open" on a row. The consumer is
   * responsible for opening the CitationDrawer.
   */
  onOpenChunk: (chunk: RetrievedChunk) => void;
  className?: string;
}

const STRIP_HEADING_LIVE = 'Sources';
const STRIP_HEADING_DEMO = 'Sources (demo)';
const OPEN_LABEL = 'Open';

/**
 * Truncate a citation to a short label suitable for the strip row. Cuts
 * at the first period or `cap` characters, whichever is shorter.
 *
 * Pure helper.
 */
export function shortCitationLabel(source: string, cap = 80): string {
  const trimmed = source.trim();
  const periodIdx = trimmed.indexOf('.');
  const cut = periodIdx > 0 && periodIdx < cap ? periodIdx : cap;
  if (trimmed.length <= cut) return trimmed;
  return `${trimmed.slice(0, cut).trim()}.`;
}

/**
 * Format a relevance score as a percent string for the visible pill.
 */
export function formatRelevance(relevance: number): string {
  const pct = Math.round(Math.max(0, Math.min(1, relevance)) * 100);
  return `${pct}%`;
}

/**
 * Compute the cosine distance pill copy from a chunk. The corpus
 * retrieval module exposes `relevance_score = 1 - distance`, so distance
 * is `1 - relevance_score`, clamped at [0, 2] (the pgvector cosine
 * distance domain).
 */
export function formatDistance(relevance: number): string {
  const distance = Math.max(0, Math.min(2, 1 - relevance));
  return distance.toFixed(2);
}

export function CitationStrip({
  citations,
  nbaId,
  onOpenChunk,
  className,
}: CitationStripProps) {
  const headingId = useId();
  // The strip is rendered only when there is at least one chunk; the
  // empty-state branch is owned by the consumer (NbaCard) per the HC3
  // contract above.
  if (citations.chunks.length === 0) return null;

  const heading =
    citations.source === 'demo' ? STRIP_HEADING_DEMO : STRIP_HEADING_LIVE;

  const baseClasses = 'flex flex-col gap-1';
  const composed = [baseClasses, className ?? ''].filter(Boolean).join(' ');

  return (
    <section
      role="group"
      aria-labelledby={headingId}
      data-testid={`nba-card-${nbaId}-citation-strip`}
      data-citation-source={citations.source}
      data-citation-count={citations.chunks.length}
      className={composed}
    >
      <h4
        id={headingId}
        data-testid={`nba-card-${nbaId}-citation-strip-heading`}
        className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary"
      >
        {heading}
      </h4>
      <ul className="flex flex-col gap-1">
        {citations.chunks.map((chunk) => (
          <CitationRow
            key={chunk.chunk_id}
            chunk={chunk}
            nbaId={nbaId}
            onOpen={() => onOpenChunk(chunk)}
          />
        ))}
      </ul>
    </section>
  );
}

interface CitationRowProps {
  chunk: RetrievedChunk;
  nbaId: string;
  onOpen: () => void;
}

function CitationRow({ chunk, nbaId, onOpen }: CitationRowProps) {
  const label = shortCitationLabel(chunk.source_citation);
  const relevancePct = formatRelevance(chunk.relevance_score);
  const distance = formatDistance(chunk.relevance_score);
  const ariaLabel = `Open citation: ${chunk.source_citation}`;

  return (
    <li
      data-testid={`nba-card-${nbaId}-citation-row-${chunk.chunk_id}`}
      data-chunk-id={chunk.chunk_id}
      className="flex flex-wrap items-center gap-gutter-sm text-signal-xs text-text-tertiary"
    >
      <span
        data-testid={`nba-card-${nbaId}-citation-row-${chunk.chunk_id}-label`}
        className="font-medium text-text-secondary"
      >
        {label}
      </span>
      {chunk.section ? (
        <span
          data-testid={`nba-card-${nbaId}-citation-row-${chunk.chunk_id}-section`}
          className="italic"
        >
          {chunk.section}
        </span>
      ) : null}
      <span
        data-testid={`nba-card-${nbaId}-citation-row-${chunk.chunk_id}-distance`}
        aria-label={`Retrieval distance ${distance}, relevance ${relevancePct}`}
        title={`Retrieval distance ${distance}`}
        className="inline-flex items-center gap-1 rounded-signal-pill bg-surface-muted px-2 py-0.5 text-signal-xs text-text-secondary"
      >
        d {distance}
      </span>
      <button
        type="button"
        data-testid={`nba-card-${nbaId}-citation-row-${chunk.chunk_id}-open`}
        aria-label={ariaLabel}
        onClick={onOpen}
        className="inline-flex items-center rounded-signal-sm border border-surface-border bg-surface-base px-2 py-0.5 text-signal-xs font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
      >
        {OPEN_LABEL}
      </button>
    </li>
  );
}

export {
  STRIP_HEADING_LIVE,
  STRIP_HEADING_DEMO,
  OPEN_LABEL,
};
